import React, { useEffect, useState } from "react";
import Topbar from "../components/Topbar";
import Sidebar from "../components/Sidebar";
import styles from "../assets/css/Dashboard.module.css";
import { useNavigate } from "react-router-dom";
import { FaArrowLeft, FaHistory, FaEye } from "react-icons/fa";
import DataTable from "react-data-table-component";

function PWD() {
  const navigate = useNavigate();
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [reportPeriod, setReportPeriod] = useState({ start: "", end: "" });
  const [pwdDetails, setPWDDetails] = useState([]);
  const [filteredDetails, setFilteredDetails] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [processingHistory, setProcessingHistory] = useState([]);
  const [selectedMemoNo, setSelectedMemoNo] = useState("");
  const [historyLoading, setHistoryLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedRowData, setSelectedRowData] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");

  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchPWDDetails = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `${process.env.REACT_APP_SUPER_ADMIN_API_URL}/pwdlistwithouttoken.php`,
          {
            method: "GET",
          }
        );

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(
            `HTTP error! Status: ${response.status}. Response: ${errorText}`
          );
        }

        const data = await response.json();

        if (data && Array.isArray(data)) {
          const dataWithSl = data.map((item, index) => ({
            ...item,
            sl: index + 1,
          }));
          setPWDDetails(dataWithSl);
          setFilteredDetails(dataWithSl);
        } else {
          console.warn(
            "API response did not contain an array as expected:",
            data
          );
          setPWDDetails([]);
          setFilteredDetails([]);
        }
      } catch (err) {
        console.error("Error fetching PWDDetails:", err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchPWDDetails();
  }, []);

  const formatDate = (dateString) => {
    try {
      // Handle API date format (DD-MM-YY)
      const [day, month, year] = dateString.split("-");
      if (day && month && year) {
        // Convert 2-digit year to full year (assuming 20xx)
        const fullYear = 2000 + parseInt(year);
        const date = new Date(fullYear, parseInt(month) - 1, parseInt(day));

        // Validate the date is valid
        if (
          date.getFullYear() === fullYear &&
          date.getMonth() === parseInt(month) - 1 &&
          date.getDate() === parseInt(day)
        ) {
          return date;
        }
      }
      return null;
    } catch (error) {
      console.error("Error parsing date:", error);
      return null;
    }
  };

  const formatDisplayDate = (date) => {
    if (!date) return "";
    return date.toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  const toYMD = (dateObj) => {
    const yyyy = dateObj.getFullYear();
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    return `${yyyy}-${mm}-${dd}`;
  };

  const handleFilter = () => {
    if (!startDate || !endDate) {
      alert("Please select both start and end dates");
      return;
    }

    try {
      const start = new Date(startDate);
      const end = new Date(endDate);

      // Validate dates
      if (isNaN(start.getTime()) || isNaN(end.getTime())) {
        alert("Invalid date format");
        return;
      }

      if (start > end) {
        alert("Start date cannot be after end date");
        return;
      }
      
      start.setHours(0, 0, 0, 0);
      end.setHours(23, 59, 59, 999);

      let filtered = pwdDetails.filter((item) => {
        if (!item.memo_date) return false;
        const memoDate = new Date(item.memo_date);
        if (isNaN(memoDate.getTime())) return false;
        return memoDate >= start && memoDate <= end;
      });

      // Re-assign serial numbers after filtering
      filtered = filtered.map((item, index) => ({
        ...item,
        sl: index + 1,
      }));

      setFilteredDetails(filtered);
      setReportPeriod({
        start: startDate,
        end: endDate,
      });
    } catch (error) {
      console.error("Error filtering dates:", error);
      alert("Error filtering dates. Please try again.");
    }
  };

  const handleReset = () => {
    setStartDate("");
    setEndDate("");
    setReportPeriod({ start: "", end: "" });
    setFilteredDetails(pwdDetails);
  };

  // Add date input validation
  const handleDateChange = (e, setter) => {
    const value = e.target.value;
    if (!value) {
      setter("");
      return;
    }

    const date = new Date(value);
    if (!isNaN(date.getTime())) {
      setter(value);
    } else {
      alert("Please enter a valid date");
    }
  };

  const handleProcessingHistory = async (id, memoNo) => {
    setSelectedMemoNo(memoNo);
    setShowModal(true);
    setHistoryLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_SUPER_ADMIN_API_URL}/singlepwdhistorywithouttoken.php`,
        {
          method: "POST",
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ pwd_id: id }),
        }
      );
      const data = await response.json();
      setProcessingHistory(data.history);
    } catch (error) {
      console.error("Error fetching processing history:", error);
    }
    setHistoryLoading(false);
  };

  const closeModal = () => {
    setShowModal(false);
    setProcessingHistory([]);
    setSelectedMemoNo("");
  };

  const handleViewDetails = (rowData) => {
    setSelectedRowData(rowData);
    setShowViewModal(true);
  };

  const closeViewModal = () => {
    setShowViewModal(false);
    setSelectedRowData(null);
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
  };

  const getFilteredData = () => {
    if (!searchTerm) return filteredDetails;
    
    return filteredDetails.filter(item => 
      Object.values(item).some(value => 
        value && value.toString().toLowerCase().includes(searchTerm.toLowerCase())
      )
    );
  };

  const columns = [
    {
      name: "SL",
      selector: (row) => row.sl,
      sortable: true,
      width: "60px",
    },
    {
      name: "Type",
      selector: (row) => row.type,
      sortable: true,
      width: "150px",
      minWidth: "150px",
    },
    {
      name: "Memo No",
      selector: (row) => row.memo_no,
      sortable: true,
      width: "150px",
      minWidth: "150px",
    },
    {
      name: "Memo Date",
      selector: (row) => row.memo_date,
      sortable: true,
      width: "150px",
      minWidth: "150px",
    },
    {
      name: "Name of Works",
      selector: (row) => row.name_of_works,
      sortable: true,
    },
    {
      name: "BOC",
      cell: (row) =>
        row.resolution_copy ? (
          <a
            href={`${process.env.REACT_APP_SUPER_ADMIN_API_URL}/${row.resolution_copy}`}
            target="_blank"
            rel="noopener noreferrer"
            className="btn btn-sm btn-outline-primary"
          >
            Download
          </a>
        ) : (
          "N/A"
        ),
      selector: (row) => row.resolution_copy,
      sortable: true,
      width: "150px",
      minWidth: "150px",
    },
    {
      name: "Current Status",
      selector: (row) => `${row.current_status_user_name} (${row.current_status_department_name})`,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Status",
      selector: (row) => row.final_status,
      sortable: true,
      width: "140px",
      minWidth: "140px",
      cell: (row) => {
        const finalStatus = (row.final_status || '').toLowerCase().trim();
        if (!finalStatus) return null;
        if (finalStatus === 'complete') return <span className="badge bg-success">Complete</span>;
        if (finalStatus === 'work not started') return <span className="badge bg-secondary">Work Not Started</span>;
        if (finalStatus === 'payment done') return <span className="badge bg-info text-dark">Payment Done</span>;
        if (finalStatus === 'pending') return <span className="badge bg-warning text-dark">Pending</span>;
        return null;
      },
    },
    {
      name: "Action",
      cell: (row) => (
        <div className="d-flex gap-1">
          <button
            onClick={() => handleViewDetails(row)}
            className="btn btn-primary btn-sm"
            title="View Details"
          >
            <FaEye />
          </button>
          <button
            onClick={() => handleProcessingHistory(row.id, row.memo_no)}
            className="btn btn-info btn-sm"
            title="View History"
          >
            <FaHistory />
          </button>
        </div>
      ),
      width: "140px",
      minWidth: "140px",
    },
    {
      name: "Alotment(Head)",
      selector: (row) => row.allotment_head_name,
      sortable: true,
      width: "150px",
      minWidth: "150px",
    },
    
    {
      name: "Alotment Amount",
      selector: (row) => row.alotment_amount,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    
    {
      name: "Less percentage",
      selector: (row) => row.lessper,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Above percentage",
      selector: (row) => row.aboveper,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    
    {
      name: "Final Amount",
      selector: (row) => row.final_amount,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Work Order Memo No",
      selector: (row) => row.work_order_meno,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Work Order Date",
      selector: (row) => row.work_order_date,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Work Order Agency",
      selector: (row) => row.engaged_agency,
      sortable: true,
      width: "250px",
      minWidth: "250px",
    },
    {
      name: "Work Value",
      selector: (row) => row.estimet_amount,
      sortable: true,
      width: "150px",
      minWidth: "150px",
    },
    {
      name: "Date of Agreement",
      selector: (row) => row.date_of_agreement,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Duration",
      selector: (row) => row.duration,
      sortable: true,
      width: "150px",
      minWidth: "150px",
    },
    {
      name: "Date of Commencement",
      selector: (row) => row.dateof_commencement,
      sortable: true,
      width: "250px",
      minWidth: "250px",
    },
    {
      name: "Date of Completion",
      selector: (row) => row.dateof_completion,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Name of Concerned SAE",
      selector: (row) => row.name_of_concerned_name,
      sortable: true,
      width: "300px",
      minWidth: "300px",
    },
    {
      name: "Department Date",
      selector: (row) => row.processing_recieving_date,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Department",
      selector: (row) => row.current_status_department_name,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Physical Status",
      selector: (row) => row.physical_status,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Final Payment Amount",
      selector: (row) => row.final_payment_amount,
      sortable: true,
      width: "250px",
      minWidth: "250px",
    },
    {
      name: "Unspent Balance",
      selector: (row) => row.unspent_balance,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
    {
      name: "Remarks",
      selector: (row) => row.remarks,
      sortable: true,
      width: "200px",
      minWidth: "200px",
    },
  
  ];

  const customStyles = {
    headRow: {
      style: {
        backgroundColor: "#f8f9fa",
        borderBottom: "1px solid #dee2e6",
        whiteSpace: "nowrap",
        minHeight: "60px",
      },
    },
    headCells: {
      style: {
        padding: "12px 16px",
        fontSize: "0.875rem",
        fontWeight: "500",
        color: "#495057",
        whiteSpace: "nowrap",
        textAlign: "left",
      },
    },
    cells: {
      style: {
        padding: "12px 16px",
        fontSize: "0.875rem",
        whiteSpace: "nowrap",
      },
    },
    table: {
      style: {
        minWidth: "100%",
      },
    },
  };

  return (
    <>
      <Topbar />
      <div className="d-flex">
        <Sidebar />
        <div className={styles.dashboardContainer}>
          <div className={styles.dashboardContent}>
            {/* Header Section */}
            <div className="d-flex align-items-center mb-4">
              <button
                className="btn btn-link text-primary p-0 me-3"
                onClick={() => navigate("/dashboard")}
                style={{ textDecoration: "none" }}
              >
                <FaArrowLeft className="me-1" />
                Back
              </button>
              <h2
                className="mb-0"
                style={{
                  color: "#2c3e50",
                  fontWeight: "500",
                  fontSize: "1.5rem",
                }}
              >
                PWD Details
              </h2>
            </div>

            {/* Filter Section */}
            <div
              className="card mb-4"
              style={{
                borderRadius: "8px",
                backgroundColor: "#fff",
              }}
            >
              <div className="card-body p-3">
                <div className="row g-3 align-items-end">
                  <div className="col-auto">
                    <label className="form-label small text-muted mb-1">
                      Start Date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={startDate}
                      onChange={(e) => handleDateChange(e, setStartDate)}
                      style={{
                        width: "160px",
                        borderColor: "#dee2e6",
                      }}
                    />
                  </div>
                  <div className="col-auto">
                    <label className="form-label small text-muted mb-1">
                      End Date
                    </label>
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      value={endDate}
                      onChange={(e) => handleDateChange(e, setEndDate)}
                      style={{
                        width: "160px",
                        borderColor: "#dee2e6",
                      }}
                    />
                  </div>
                  <div className="col-auto">
                    <button
                      className="btn btn-primary btn-sm px-4"
                      onClick={handleFilter}
                      disabled={!startDate || !endDate}
                    >
                      Apply Filter
                    </button>
                    <button
                      className="btn btn-outline-secondary btn-sm px-3 ms-2"
                      onClick={handleReset}
                    >
                      Reset
                    </button>
                  </div>
                </div>
              </div>
            </div>

            {/* Table Section */}
            <div className="card">
              <div className="card-body p-0">
                {loading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : (
                  <>
                    {/* Search Section */}
                    <div className="p-3 border-bottom">
                      <div className="row align-items-center">
                        <div className="col-md-6">
                          <div className="input-group">
                            <span className="input-group-text">
                              🔍
                            </span>
                            <input
                              type="text"
                              className="form-control"
                              placeholder="Search in all fields..."
                              value={searchTerm}
                              onChange={handleSearch}
                            />
                            {searchTerm && (
                              <button
                                className="btn btn-outline-secondary"
                                type="button"
                                onClick={() => setSearchTerm("")}
                              >
                                ✕
                              </button>
                            )}
                          </div>
                        </div>
                        <div className="col-md-6 text-end">
                          <small className="text-muted">
                            {getFilteredData().length} of {filteredDetails.length} records
                          </small>
                        </div>
                      </div>
                    </div>
                    
                    <DataTable
                      columns={columns}
                      data={getFilteredData()}
                      pagination
                      paginationPerPage={10}
                      paginationRowsPerPageOptions={[10, 20, 30, 50]}
                      highlightOnHover
                      pointerOnHover
                      customStyles={customStyles}
                      noDataComponent={
                        <div className="text-center py-4 text-muted">
                          {searchTerm ? "No data found matching your search" : "No data found for the selected date range"}
                        </div>
                      }
                    />
                  </>
                )}
                <div className="p-3 border-top">
                  <p
                    className="mb-0 text-muted"
                    style={{
                      fontSize: "0.875rem",
                      borderLeft: "3px solid #007bff",
                      paddingLeft: "10px",
                    }}
                  >
                    <i className="far fa-calendar-alt me-2"></i>
                    Report generated for the period: {reportPeriod.start ||
                      "-"}{" "}
                    to {reportPeriod.end || "-"}
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Processing History Modal */}
      {showModal && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                Work Order No : {selectedMemoNo}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeModal}
                ></button>
              </div>
              <div className="modal-body">
                {historyLoading ? (
                  <div className="text-center py-4">
                    <div className="spinner-border text-primary" role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                  </div>
                ) : processingHistory?.length === 0 ? (
                  <div className="text-center py-4 text-muted">
                    No processing history found
                  </div>
                ) : (
                  <div className="table-responsive">
                  <table className="table table-bordered table-striped align-middle">
                    <thead className="table-dark">
                      <tr style={{ textTransform: 'capitalize' }}>
                        <th>Department Name</th>
                        <th>Concerned Name</th>
                        <th>Date</th>
                        <th>Type</th>
                        <th>Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {processingHistory?.sort((a, b) => {
                        // Sort by creating_date in ascending order (oldest first)
                        const dateA = new Date(a.creating_date);
                        const dateB = new Date(b.creating_date);
                        return dateA - dateB;
                      }).map((history, index) => {
                        const showToStatus = history.to_status && history.to_status.trim() !== '';
                        return (
                          <tr key={index} style={{ textTransform: 'capitalize' }}>
                            <td>{history.department_name}</td>
                            <td>{history.user_name}</td>
                            <td>{history.creating_date}</td>
                            <td>{history.type}</td>
                            <td>
                              {showToStatus ? (
                                <span style={{ textTransform: 'capitalize', fontSize: '1em' }}>{history.to_status} by {history.by_user_name}</span>
                              ) : null}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Details Modal */}
      {showViewModal && selectedRowData && (
        <div
          className="modal fade show"
          style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }}
        >
          <div className="modal-dialog modal-xl">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  PWD Details for Memo No: {selectedRowData.memo_no}
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  onClick={closeViewModal}
                ></button>
              </div>
              <div className="modal-body">
                <div className="table-responsive">
                  <table className="table table-bordered table-striped">
                    <tbody>
                                            {Object.entries(selectedRowData)
                        .filter(([key]) => !['send_to_id', 'receiver_id', 'current_status', 'current_status_department_id', 'sl', 'id', 'name_of_concerned_id'].includes(key))
                        .map(([key, value]) => {
                          // Custom field name mapping
                          let displayName = key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                          if (key === 'aboveper') displayName = 'Above Percentage';
                          if (key === 'lessper') displayName = 'Less Percentage';
                          if (key === 'dateof_commencement') displayName = 'Date of Commencement';
                          if (key === 'dateof_completion') displayName = 'Date of Completion';
                          
                          return (
                            <tr key={key}>
                              <td className="fw-bold" style={{width: "30%"}}>
                                {displayName}
                              </td>
                              <td>
                                {key === 'resolution_copy' && value ? (
                                  <a
                                    href={`${process.env.REACT_APP_SUPER_ADMIN_API_URL}/${value}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn btn-sm btn-outline-primary"
                                  >
                                    Download
                                  </a>
                                ) : key === 'final_status' && value ? (
                                  <span className={`badge ${
                                    value.toLowerCase() === 'complete' ? 'bg-success' :
                                    value.toLowerCase() === 'work not started' ? 'bg-secondary' :
                                    value.toLowerCase() === 'payment done' ? 'bg-info text-dark' :
                                    value.toLowerCase() === 'pending' ? 'bg-warning text-dark' : 'bg-secondary'
                                  }`}>
                                    {value}
                                  </span>
                                ) : (
                                  value || 'N/A'
                                )}
                              </td>
                            </tr>
                          );
                        })}
                    </tbody>
                  </table>
                </div>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={closeViewModal}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PWD;
